import { AlarmFill, ArrowRightLong, CloseMiniLine } from '@repo/go-icons';
import { Button, LoaderIcon } from '@repo/go-ui';
import { Progress } from 'antd';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { hasValue } from 'src/shared/utils/common';
import type { TPlanVersion } from 'src/store/comparison/types';
import type { ImportJob } from 'src/store/jobs/import-job';

import styles from './import-item.module.scss';

type Props = {
  job: ImportJob;
  parentPlanVersion: TPlanVersion | null;
  onCancel(): void;
  onContinue(): void;
};

export const ImportItem = observer<Props>(function ImportItem({ job, parentPlanVersion, onCancel, onContinue }) {
  const { t } = useTranslation();

  const processedRows = job.result?.processedRows;
  const totalRows = job.result?.totalRows;

  const hasResult = hasValue(processedRows) && hasValue(totalRows) && totalRows > 0;
  const isInLoading = job.status === 'NEW' || (job.status === 'IN_PROGRESS' && !hasResult);
  const isInProgress = job.status === 'IN_PROGRESS' && hasResult;
  const isCompletedSuccessfully = job.status === 'COMPLETED' && !job.errorsCount;
  const isCompletedWithError = job.status === 'COMPLETED' && !!job.errorsCount;

  if (!isInLoading && !isInProgress && !isCompletedSuccessfully && !isCompletedWithError) {
    return null;
  }

  return (
    <div
      className={clsx(
        styles.wrapper,
        !parentPlanVersion && styles.wrapper__narrowed,
        isInLoading && styles.wrapper__inLoading,
        isInProgress && styles.wrapper__inProgress,
        isCompletedWithError && styles.wrapper__completedWithError,
        isCompletedSuccessfully && styles.wrapper__completed,
      )}
    >
      {isInLoading && <LoaderIcon />}
      {isCompletedWithError && <AlarmFill className={styles.exclamationMarkIcon} />}
      <div className={styles.contentWrapper}>
        {parentPlanVersion && <p className={styles.planVersionName}>{parentPlanVersion.data.name}</p>}
        <div
          className={clsx(
            styles.importInfoWrapper,
            isInLoading && styles.importInfoWrapper__inLoading,
            isInProgress && styles.importInfoWrapper__inProgress,
            isCompletedWithError && styles.importInfoWrapper__completedWithError,
            isCompletedSuccessfully && styles.importInfoWrapper__completed,
          )}
        >
          {isInLoading && (
            <>
              <p className={styles.importInfo}>{t('drillingCarpet:ImportProgress.initializing')}</p>

              <Button
                icon={<CloseMiniLine />}
                iconPosition="before"
                className={styles.cancelImportButton}
                onClick={onCancel}
              >
                {t('common:Buttons.cancel')}
              </Button>
            </>
          )}

          {isInProgress && (
            <>
              <div>
                <div className={styles.importInfo}>
                  {t('drillingCarpet:ImportProgress.importedWells', { processed: processedRows, total: totalRows })}
                </div>

                {processedRows !== undefined && totalRows !== undefined && (
                  <Progress
                    className={styles.progressBar}
                    percent={(processedRows / totalRows) * 100}
                    strokeWidth={2}
                    showInfo={false}
                    strokeColor="#7471FF"
                    style={{ color: '#7471FF' }}
                  />
                )}
              </div>

              <Button
                icon={<CloseMiniLine />}
                iconPosition="before"
                className={styles.cancelImportButton}
                onClick={onCancel}
              >
                {t('common:Buttons.cancel')}
              </Button>
            </>
          )}

          {isCompletedWithError && (
            <>
              <div className={styles.importInfo}>
                {t('drillingCarpet:ImportProgress.wellsInConflict', { count: job.errorsCount, total: totalRows })}
              </div>

              <Button
                variant="flat"
                style={{
                  padding: '0px 8px',
                }}
                className={styles.goToImportButton}
                icon={<ArrowRightLong />}
                onClick={onContinue}
              >
                {t('drillingCarpet:ImportProgress.goTo')}
              </Button>
            </>
          )}

          {isCompletedSuccessfully && (
            <>
              <div className={styles.importInfo}>
                {t('drillingCarpet:ImportProgress.importedWells', { processed: processedRows, total: totalRows })}
              </div>

              <Button variant="flat" className={styles.goToImportButton} icon={<ArrowRightLong />} onClick={onContinue}>
                {t('drillingCarpet:ImportProgress.goTo')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
});
